import { useCallback, useMemo } from "react";
import { isAuthProviderOidc } from "../utils/auth";
import useOidcAuth from "./useOidcAuth";
import useAuthenticationTokens from "./useAuthenticationTokens";
import { useAuth0 } from "@auth0/auth0-react";

const isOidc = isAuthProviderOidc();
// Injected by index.html (server-side include of the http_authorization header).
// The `Bearer` check matters: a server that does not process the include leaves
// the raw directive behind, and this flag decides whether Auth0 is used at all.
export const isAlternativeFlow = !!(
  window as Window & {
    __JWT_TOKEN__?: string;
  }
).__JWT_TOKEN__?.startsWith("Bearer");

const useAuth = () => {
  const oidcAuth = useOidcAuth();
  const auth0Auth = useAuth0();
  const { accessToken } = useAuthenticationTokens();

  const getAccessTokenSilently = useCallback(
    (alwaysGetNewToken: boolean = false) => {
      if (isOidc) {
        return oidcAuth.getAccessTokenSilently(alwaysGetNewToken);
      }

      const cacheModeValue = alwaysGetNewToken ? "off" : "on";
      return auth0Auth.getAccessTokenSilently({ cacheMode: cacheModeValue });
    },
    [oidcAuth, auth0Auth],
  );

  const loginWithRedirect = useCallback(
    (previousPath: string) => {
      if (isOidc) {
        return oidcAuth.login(previousPath);
      }

      return auth0Auth.loginWithRedirect({
        appState: {
          returnTo: previousPath,
        },
      });
    },
    [oidcAuth, auth0Auth],
  );

  // TODO Not fully working with OIDC
  const loginWithInvite = useCallback(
    (invitation: string, organization: string) => {
      if (isOidc) {
        return oidcAuth.loginWithRedirect(invitation, organization);
      }

      return auth0Auth.loginWithRedirect({
        authorizationParams: { invitation, organization },
      });
    },
    [oidcAuth, auth0Auth],
  );

  const logout = useCallback(() => {
    return isOidc
      ? oidcAuth.logout()
      : auth0Auth.logout({
          logoutParams: { returnTo: window.location.origin },
        });
  }, [oidcAuth, auth0Auth]);

  const isLoading = useMemo(() => {
    return isOidc ? oidcAuth.isLoading : auth0Auth.isLoading;
  }, [oidcAuth, auth0Auth]);

  const isAuthenticated = useMemo(() => {
    if (accessToken && isAlternativeFlow) {
      return true;
    }
    return isOidc ? oidcAuth.isAuthenticated : auth0Auth.isAuthenticated;
  }, [oidcAuth, auth0Auth, accessToken]);

  return {
    getAccessTokenSilently,
    loginWithRedirect,
    loginWithInvite,
    logout,
    isLoading,
    isAuthenticated,
  };
};

export default useAuth;
